import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { createSubscription } from "../../services/subscriptionService"
import { getPacks } from "../../services/packService"
import { getSubscriptionPlans, getSubscriptionPlansForPack } from "../../services/subscriptionPlanService"
import { fr } from "../../locales/fr"
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table"

export default function StepSubscription({ member, next, setSubscription }) {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    pack_id: "",
    plan_id: "",
    start_date: "",
  })
  const [packs, setPacks] = useState([])
  const [planTemplates, setPlanTemplates] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [loadError, setLoadError] = useState("")

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError("")
    Promise.all([getPacks(), getSubscriptionPlans()])
      .then(([p, pl]) => {
        if (!cancelled) {
          setPacks(Array.isArray(p) ? p : [])
          setPlanTemplates(Array.isArray(pl) ? pl : [])
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(fr.wizard.stepSubscription.loadOptionsError)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!form.pack_id) {
      setPlans([])
      return
    }
    let cancelled = false
    setLoadingPlans(true)
    getSubscriptionPlansForPack(form.pack_id)
      .then((data) => {
        if (!cancelled) setPlans(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setPlans([])
      })
      .finally(() => {
        if (!cancelled) setLoadingPlans(false)
      })
    return () => {
      cancelled = true
    }
  }, [form.pack_id])

  const w = fr.wizard.stepSubscription

  const selectedPack = packs.find((p) => String(p.id) === String(form.pack_id))

  const getEndDate = () => {
    if (!form.start_date || !form.plan_id) return ""
    const plan = plans.find((p) => String(p.id) === String(form.plan_id))
    if (!plan) return ""
    const date = new Date(form.start_date)
    date.setMonth(date.getMonth() + plan.durationMonths)
    return date.toISOString().split("T")[0]
  }

  const getPrice = () => {
    if (!form.pack_id || !form.plan_id) return 0
    const pack = packs.find((p) => String(p.id) === String(form.pack_id))
    const plan = plans.find((p) => String(p.id) === String(form.plan_id))
    if (!pack || !plan) return 0
    return pack.price + plan.price
  }

  const getFormulaPrice = () => {
    if (!form.plan_id) return 0
    const plan = plans.find((p) => String(p.id) === String(form.plan_id))
    return plan ? plan.price : 0
  }

  const save = async () => {
    const sub = await createSubscription({
      member_id: member.id,
      pack_id: Number(form.pack_id),
      plan_id: Number(form.plan_id),
      start_date: form.start_date,
    })

    setSubscription(sub)

    next(3)
  }

  const tileBase =
    "cursor-pointer rounded-lg border p-4 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  const tileIdle = "border-border bg-card hover:border-muted-foreground/40"
  const tileActive = "border-primary bg-primary/10 shadow-sm ring-1 ring-primary/30"

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">{w.loadingOptions}</CardContent>
      </Card>
    )
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="space-y-4 py-8 text-center">
          <p className="text-destructive">{loadError}</p>
          <Button type="button" variant="outline" onClick={() => navigate("/settings/packs")}>
            {fr.settings.packs.manageLink}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (packs.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-4 py-8 text-center">
          <p className="text-muted-foreground">{w.noPacks}</p>
          <Button type="button" onClick={() => navigate("/settings/packs")}>
            {fr.settings.packs.manageLink}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (planTemplates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{w.title}</CardTitle>
          <CardDescription>
            {member.firstName} {member.lastName}
          </CardDescription>
        </CardHeader>
        <CardContent className="py-8 text-center text-muted-foreground">
          <p>{w.noPlans}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{w.title}</CardTitle>
        <CardDescription className="space-y-2">
          <span>
            {member.firstName} {member.lastName}
          </span>
          <span className="block text-muted-foreground">{w.helper}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">{w.packTitle}</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:max-w-3xl">
            {packs.map((p) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => setForm({ ...form, pack_id: p.id, plan_id: "" })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setForm({ ...form, pack_id: p.id, plan_id: "" })
                }}
                className={`${tileBase} ${form.pack_id === p.id ? tileActive : tileIdle}`}
              >
                <p className="font-medium text-foreground">{p.name}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {p.price} MAD {w.perMonth}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">{w.planTitle}</h3>
          {!form.pack_id ? (
            <p className="text-sm text-muted-foreground">{w.pickPackFirst}</p>
          ) : loadingPlans ? (
            <p className="text-sm text-muted-foreground">{w.loadingPlansForPack}</p>
          ) : plans.length === 0 ? (
            <p className="text-sm text-muted-foreground">{w.noPlans}</p>
          ) : (
            <div className="space-y-6">
              {selectedPack ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">{w.pricingTableTitle}</p>
                  <p className="text-xs text-muted-foreground">{w.pricingTableHint}</p>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{w.colPlanName}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{w.colDuration}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{w.colFormulaPrice}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{w.colTotal}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {plans.map((pl) => {
                          const total = selectedPack.price + pl.price
                          const selected = String(form.plan_id) === String(pl.id)
                          return (
                            <TableRow
                              key={pl.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => setForm({ ...form, plan_id: pl.id })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  setForm({ ...form, plan_id: pl.id })
                              }}
                              className={`cursor-pointer transition-colors ${
                                selected ? "bg-primary/15 hover:bg-primary/20" : "hover:bg-muted/50"
                              }`}
                            >
                              <TableCell className="font-medium text-foreground">{pl.name}</TableCell>
                              <TableCell className="text-right tabular-nums text-muted-foreground">
                                {pl.durationMonths} mois
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {Number(pl.price).toLocaleString()} MAD
                              </TableCell>
                              <TableCell className="text-right font-semibold tabular-nums text-foreground">
                                {Number(total).toLocaleString()} MAD
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : null}

              <div>
                <p className="mb-3 text-xs text-muted-foreground">{w.planCardsHint}</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:max-w-3xl">
                  {plans.map((p) => {
                    const total = selectedPack ? selectedPack.price + p.price : 0
                    return (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setForm({ ...form, plan_id: p.id })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") setForm({ ...form, plan_id: p.id })
                        }}
                        className={`${tileBase} ${form.plan_id === p.id ? tileActive : tileIdle}`}
                      >
                        <span className="font-medium text-foreground">{p.name}</span>
                        <p className="mt-2 text-base font-semibold tabular-nums text-foreground">
                          {w.tileTotalLabel} {Number(total).toLocaleString()} MAD
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {p.durationMonths} mois · {w.tileFormulaDetail} {Number(p.price).toLocaleString()}{" "}
                          MAD
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-md space-y-2">
          <Label htmlFor="wiz-start-date">{w.startDate}</Label>
          <Input
            id="wiz-start-date"
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          />
        </div>

        {form.pack_id && form.plan_id && form.start_date ? (
          <div className="max-w-xl rounded-lg border border-border bg-muted/40 p-5 text-sm">
            <p className="mb-3 font-semibold text-foreground">{w.summaryTitle}</p>
            <p className="text-muted-foreground">
              <span className="text-foreground">{w.packLine}</span> :{" "}
              {packs.find((p) => String(p.id) === String(form.pack_id))?.name}
            </p>
            <p className="text-muted-foreground">
              <span className="text-foreground">{w.planLine}</span> :{" "}
              {plans.find((p) => String(p.id) === String(form.plan_id))?.name}
            </p>
            <p className="text-muted-foreground">
              <span className="text-foreground">{w.startLine}</span> : {form.start_date}
            </p>
            <p className="text-muted-foreground">
              <span className="text-foreground">{w.endLine}</span> : {getEndDate()}
            </p>
            <p className="mt-2 text-muted-foreground">
              <span className="text-foreground">{w.packBaseLine}</span> :{" "}
              {packs.find((p) => String(p.id) === String(form.pack_id))?.price} MAD
            </p>
            <p className="text-muted-foreground">
              <span className="text-foreground">{w.formulaLine}</span> : {getFormulaPrice()} MAD
            </p>
            <p className="mt-3 font-semibold text-foreground">
              {w.priceLine} : {getPrice()} MAD
            </p>
          </div>
        ) : null}
      </CardContent>
      <CardFooter className="flex justify-end border-t border-border pt-6">
        <Button
          type="button"
          onClick={save}
          disabled={!form.pack_id || !form.plan_id || !form.start_date || loadingPlans}
        >
          {w.next}
        </Button>
      </CardFooter>
    </Card>
  )
}
